import L from 'leaflet';
import 'leaflet-imageoverlay-rotated';
import { API_BASE_URL } from './utils';
import type { EventMapOverlayMeta } from '../types';

/** Overlay config with a client-resolved image URL for Leaflet. */
export interface EventMapOverlay extends EventMapOverlayMeta {
  imageUrl: string;
}

export type EventMapOverlaySavePayload = EventMapOverlayMeta & {
  imageDataUrl?: string;
};

export const DEFAULT_OVERLAY_OPACITY = 0.55;
export const DEFAULT_OVERLAY_WIDTH_M = 400;

export function mdtOverlayVisibleKey(eventId: string | null | undefined): string {
  return eventId ? `c2_mdt_site_map_visible_${eventId}` : 'c2_mdt_site_map_visible';
}

export function loadMdtOverlayVisible(eventId: string | null | undefined): boolean {
  try {
    const raw = sessionStorage.getItem(mdtOverlayVisibleKey(eventId));
    if (raw === null) return true;
    return raw === '1';
  } catch {
    return true;
  }
}

export function saveMdtOverlayVisible(eventId: string | null | undefined, visible: boolean): void {
  try {
    sessionStorage.setItem(mdtOverlayVisibleKey(eventId), visible ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function metersToLatOffset(meters: number): number {
  return meters / 111320;
}

function metersToLngOffset(meters: number, lat: number): number {
  const cosLat = Math.cos((lat * Math.PI) / 180);
  return meters / (111320 * (cosLat || 1));
}

/** Move overlay anchor by east (+) / west (-) and north (+) / south (-) metres. */
export function offsetOverlayCenter(
  centerLat: number,
  centerLng: number,
  eastMeters: number,
  northMeters: number
): { centerLat: number; centerLng: number } {
  return {
    centerLat: centerLat + metersToLatOffset(northMeters),
    centerLng: centerLng + metersToLngOffset(eastMeters, centerLat),
  };
}

export function computeOverlayCorners(
  centerLat: number,
  centerLng: number,
  widthM: number,
  heightM: number,
  rotationDeg: number
): { topLeft: L.LatLng; topRight: L.LatLng; bottomLeft: L.LatLng } {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const halfW = widthM / 2;
  const halfH = heightM / 2;

  const localCorners = [
    { x: -halfW, y: halfH },
    { x: halfW, y: halfH },
    { x: -halfW, y: -halfH },
  ];

  const rotate = (x: number, y: number) => ({
    x: x * cos - y * sin,
    y: x * sin + y * cos,
  });

  const toLatLng = (x: number, y: number) =>
    L.latLng(
      centerLat + metersToLatOffset(y),
      centerLng + metersToLngOffset(x, centerLat)
    );

  const tl = rotate(localCorners[0].x, localCorners[0].y);
  const tr = rotate(localCorners[1].x, localCorners[1].y);
  const bl = rotate(localCorners[2].x, localCorners[2].y);

  return {
    topLeft: toLatLng(tl.x, tl.y),
    topRight: toLatLng(tr.x, tr.y),
    bottomLeft: toLatLng(bl.x, bl.y),
  };
}

export type RotatedOverlayLayer = L.ImageOverlay & {
  reposition: (
    topleft: L.LatLngExpression,
    topright: L.LatLngExpression,
    bottomleft: L.LatLngExpression
  ) => void;
  setUrl: (url: string) => void;
};

declare module 'leaflet' {
  namespace imageOverlay {
    function rotated(
      image: string | HTMLImageElement | HTMLCanvasElement,
      topleft: LatLngExpression,
      topright: LatLngExpression,
      bottomleft: LatLngExpression,
      options?: ImageOverlayOptions
    ): RotatedOverlayLayer;
  }
}

export const SITE_MAP_PANE = 'siteMapPane';

/** Dedicated pane above base tiles but below markers — site plan stays visible. */
export function ensureSiteMapPane(map: L.Map): string {
  if (!map.getPane(SITE_MAP_PANE)) {
    map.createPane(SITE_MAP_PANE);
    const pane = map.getPane(SITE_MAP_PANE)!;
    pane.style.zIndex = '450';
    pane.style.pointerEvents = 'none';
  }
  return SITE_MAP_PANE;
}

export function createOverlayLayer(map: L.Map, config: EventMapOverlay): RotatedOverlayLayer {
  ensureSiteMapPane(map);
  const { topLeft, topRight, bottomLeft } = computeOverlayCorners(
    config.centerLat,
    config.centerLng,
    config.widthMeters,
    config.heightMeters,
    config.rotation
  );

  const layer = L.imageOverlay.rotated(config.imageUrl, topLeft, topRight, bottomLeft, {
    opacity: config.opacity,
    interactive: false,
    pane: SITE_MAP_PANE,
    className: 'c2-site-map-overlay',
    zIndex: 1,
  });

  layer.addTo(map);
  layer.bringToFront();
  return layer;
}

export function updateOverlayLayer(layer: RotatedOverlayLayer, config: EventMapOverlay): void {
  const { topLeft, topRight, bottomLeft } = computeOverlayCorners(
    config.centerLat,
    config.centerLng,
    config.widthMeters,
    config.heightMeters,
    config.rotation
  );
  layer.setOpacity(config.opacity);
  if (layer.setUrl && config.imageUrl) {
    layer.setUrl(config.imageUrl);
  }
  layer.reposition(topLeft, topRight, bottomLeft);
  layer.bringToFront();
}

export async function readImageFileAsDataUrl(
  file: File
): Promise<{ dataUrl: string; width: number; height: number }> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });

  const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Invalid image file'));
    img.src = dataUrl;
  });

  return { dataUrl, ...dimensions };
}

export function buildDefaultOverlayMeta(
  centerLat: number,
  centerLng: number,
  imageWidth: number,
  imageHeight: number
): EventMapOverlayMeta {
  const aspect = imageHeight / imageWidth;
  const widthMeters = DEFAULT_OVERLAY_WIDTH_M;
  return {
    centerLat,
    centerLng,
    widthMeters,
    heightMeters: widthMeters * aspect,
    rotation: 0,
    opacity: DEFAULT_OVERLAY_OPACITY,
  };
}

/** Size and centre an overlay to match the current map viewport. */
export function overlayMetaFitToMapView(
  map: L.Map,
  imageWidthPx: number,
  imageHeightPx: number,
  fillRatio = 0.88
): EventMapOverlayMeta {
  const bounds = map.getBounds();
  const center = map.getCenter();
  const latMid = center.lat;
  const viewHeightM = Math.abs(bounds.getNorth() - bounds.getSouth()) * 111320;
  const viewWidthM =
    Math.abs(bounds.getEast() - bounds.getWest()) *
    111320 *
    Math.cos((latMid * Math.PI) / 180);

  const aspect = imageHeightPx / imageWidthPx;
  let widthMeters = viewWidthM * fillRatio;
  let heightMeters = widthMeters * aspect;

  if (heightMeters > viewHeightM * fillRatio) {
    heightMeters = viewHeightM * fillRatio;
    widthMeters = heightMeters / aspect;
  }

  return {
    centerLat: center.lat,
    centerLng: center.lng,
    widthMeters: Math.max(15, widthMeters),
    heightMeters: Math.max(15, heightMeters),
    rotation: 0,
    opacity: DEFAULT_OVERLAY_OPACITY,
  };
}

export function overlayScaleLimitsForMap(map: L.Map): { min: number; max: number } {
  const bounds = map.getBounds();
  const latMid = bounds.getCenter().lat;
  const viewWidthM =
    Math.abs(bounds.getEast() - bounds.getWest()) *
    111320 *
    Math.cos((latMid * Math.PI) / 180);
  return {
    min: 10,
    max: Math.max(100, Math.round(viewWidthM * 2)),
  };
}

export async function fetchEventMapOverlay(
  eventId: string,
  token: string
): Promise<EventMapOverlay | null> {
  const eventRes = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!eventRes.ok) return null;
  const eventData = await eventRes.json();
  const meta = eventData.event?.mapOverlay as EventMapOverlayMeta | undefined;
  if (!meta?.imageFile) return null;

  const imgRes = await fetch(
    `${API_BASE_URL}/events/${encodeURIComponent(eventId)}/map-overlay/image`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!imgRes.ok) return null;
  const blob = await imgRes.blob();
  const imageUrl = URL.createObjectURL(blob);
  return { ...meta, imageUrl };
}

export async function saveEventMapOverlayApi(
  eventId: string,
  token: string,
  payload: EventMapOverlaySavePayload
): Promise<EventMapOverlayMeta | null> {
  const res = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}/map-overlay`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to save map overlay');
  }
  const data = await res.json();
  return data.mapOverlay || null;
}

export async function deleteEventMapOverlayApi(eventId: string, token: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(eventId)}/map-overlay`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to remove map overlay');
  }
}

export function revokeOverlayImageUrl(overlay: EventMapOverlay | null): void {
  if (overlay?.imageUrl?.startsWith('blob:')) {
    URL.revokeObjectURL(overlay.imageUrl);
  }
}
